import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import { FiCheckCircle, FiAlertCircle, FiInfo, FiX, FiAlertTriangle } from 'react-icons/fi';

export function Toast({ message, type = 'success', onClose }) {
  const icons = {
    success: { icon: FiCheckCircle, color: 'text-emerald-500', bg: 'bg-emerald-50', border: 'border-emerald-100' },
    error: { icon: FiAlertCircle, color: 'text-red-500', bg: 'bg-red-50', border: 'border-red-100' },
    info: { icon: FiInfo, color: 'text-brand-blue', bg: 'bg-brand-blue/5', border: 'border-brand-blue/10' }
  };

  const theme = icons[type] || icons.success;

  React.useEffect(() => {
    const timer = setTimeout(onClose, 5000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 50, x: 50 }}
      animate={{ opacity: 1, y: 0, x: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className={`fixed bottom-8 right-8 z-[200] flex items-center gap-4 px-6 py-4 rounded-2xl shadow-2xl border ${theme.bg} ${theme.border} min-w-[320px] max-w-md`}
    >
      <div className={theme.color}>
        <SafeIcon icon={theme.icon} className="text-xl" />
      </div>
      <div className="flex-grow">
        <p className="text-xs font-black text-slate-900 uppercase tracking-tight">{type === 'error' ? 'System Error' : 'Notification'}</p>
        <p className="text-xs text-slate-600 font-medium mt-0.5">{message}</p>
      </div>
      <button onClick={onClose} className="p-1 hover:bg-black/5 rounded-lg transition-colors text-slate-400">
        <SafeIcon icon={FiX} />
      </button>
    </motion.div>
  );
}

export function ConfirmationModal({ isOpen, title, message, onConfirm, onCancel, confirmText = 'Confirm', confirmVariant = 'primary', loading = false }) {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[150] flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onCancel}
          className="absolute inset-0 bg-navy-900/60 backdrop-blur-sm"
        />
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative w-full max-w-md bg-white rounded-[2.5rem] shadow-2xl overflow-hidden p-8 sm:p-10"
        >
          <div className="flex flex-col items-center text-center">
            <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-6 ${confirmVariant === 'danger' ? 'bg-red-50 text-red-500' : 'bg-brand-blue/5 text-brand-blue'}`}>
              <SafeIcon icon={confirmVariant === 'danger' ? FiAlertTriangle : FiInfo} className="text-3xl" />
            </div>
            <h3 className="text-2xl font-black text-slate-900 tracking-tight uppercase leading-none mb-3">{title}</h3>
            <p className="text-sm text-slate-500 font-medium leading-relaxed mb-8">{message}</p>
            
            <div className="grid grid-cols-2 gap-4 w-full">
              <button
                onClick={onCancel}
                disabled={loading}
                className="py-4 bg-slate-100 text-slate-600 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-200 transition-all active:scale-95 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={onConfirm}
                disabled={loading}
                className={`py-4 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all active:scale-95 disabled:opacity-50 ${
                  confirmVariant === 'danger' ? 'bg-red-500 hover:bg-red-600 shadow-lg shadow-red-500/20' : 'bg-brand-blue hover:bg-navy-900 shadow-lg shadow-brand-blue/20'
                }`}
              >
                {loading ? 'Processing...' : confirmText}
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}